import { AppThunk } from "../app/store";
import { searchAggregations } from "../app/api";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AggregationFacet,
  AggregationResult,
  BucketResult,
  SearchAggregationResult,
} from "../../server/asin-central/search";

export type FilterOptionBucketCount = {
  [key: string]: { count: number };
};

export type FilterWithAggregatedDataMap = {
  [key: string]: FilterOptionBucketCount;
};

type FilterWithAggregatedData = {
  status?: "pending" | "loading" | "finished" | "error";
  count: number;
  error?: string | null;
  data?: FilterWithAggregatedDataMap | null;
};

type FilterWithAggregatedDataState = {
  filterWithAggregatedData: FilterWithAggregatedData;
};

const filterWithAggregatedDataInitialState: FilterWithAggregatedDataState = {
  filterWithAggregatedData: {
    status: "pending",
    count: 0,
    data: null,
  },
};

const filterWithAggregatedData = createSlice({
  name: "filterWithAggregatedData",
  initialState: filterWithAggregatedDataInitialState,
  reducers: {
    getFilterWithAggregatedDataStart(state: FilterWithAggregatedDataState) {
      state.filterWithAggregatedData.status = "loading";
    },
    getFilterWithAggregatedDataSuccess(
      state: FilterWithAggregatedDataState,
      { payload }: PayloadAction<SearchAggregationResult>
    ) {
      const dataMap: FilterWithAggregatedDataMap = {};
      if (payload.aggregationResults) {
        Object.entries(payload.aggregationResults).forEach(([k, v]) => {
          if (!dataMap[k]) {
            dataMap[k] = {};
          }
          v!.forEach((bucket: BucketResult) => {
            dataMap[k][bucket.key ?? ""] = { count: bucket.documentCount ?? 0 };
          });
        });
      }
      state.filterWithAggregatedData.data = dataMap;
      state.filterWithAggregatedData.count = payload.totalResultCount || 0;
      state.filterWithAggregatedData.status = "finished";
    },
    getFilterWithAggregatedDataFailure(
      state: FilterWithAggregatedDataState,
      action: PayloadAction<string>
    ) {
      state.filterWithAggregatedData.status = "error";
      state.filterWithAggregatedData.error = action.payload;
    },
  },
});

export const {
  getFilterWithAggregatedDataStart,
  getFilterWithAggregatedDataSuccess,
  getFilterWithAggregatedDataFailure,
} = filterWithAggregatedData.actions;

export default filterWithAggregatedData.reducer;

export const getFilterWithAggregatedData = (): AppThunk => async (dispatch) => {
  try {
    dispatch(getFilterWithAggregatedDataStart());
    const queryString = "(all)";
    const aggregationFacets: AggregationFacet[] = [
      {
        attributeName: "programId",
        aggregationType: "terms",
        totalAggregationCount: 10000, // Max number of programs to return
      },
      {
        attributeName: "productLineId",
        aggregationType: "terms",
        totalAggregationCount: 10000, // Max number of product lines to return
      },
    ];
    const result = await searchAggregations(queryString, aggregationFacets);
    dispatch(getFilterWithAggregatedDataSuccess(result));
  } catch (err: any) {
    dispatch(getFilterWithAggregatedDataFailure(err.toString()));
  }
};
